import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Radio, Loader2, TrendingUp, TrendingDown, Minus, ExternalLink, RefreshCw } from 'lucide-react';
import { apiGet, apiPost } from '../data/api';
import t from '../i18n/it';

function SportBadge({ sport }) {
  return sport === 'tennis' ? (
    <span className="px-1.5 py-0.5 text-xs rounded bg-blue-500/15 text-blue-400">
      {t.common.tennis}
    </span>
  ) : (
    <span className="px-1.5 py-0.5 text-xs rounded bg-purple-500/15 text-purple-400">
      {t.common.table_tennis}
    </span>
  );
}

function MomentumBadge({ score }) {
  if (score > 20)
    return (
      <span className="flex items-center gap-1 text-xs text-emerald-400">
        <TrendingUp size={12} /> +{score.toFixed(0)}
      </span>
    );
  if (score < -20)
    return (
      <span className="flex items-center gap-1 text-xs text-red-400">
        <TrendingDown size={12} /> {score.toFixed(0)}
      </span>
    );
  return (
    <span className="flex items-center gap-1 text-xs text-zinc-500">
      <Minus size={12} /> {score.toFixed(0)}
    </span>
  );
}

function PressureBar({ score }) {
  const color = score >= 70 ? '#EF4444' : score >= 50 ? '#F59E0B' : '#71717A';
  const pct = Math.min(score, 100);
  return (
    <div className="flex items-center gap-2">
      <div className="w-12 h-1.5 rounded-full bg-zinc-800">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span
        className="text-xs tabular-nums"
        style={{ fontFamily: 'JetBrains Mono, monospace', color }}
      >
        {score.toFixed(0)}
      </span>
    </div>
  );
}

function EvBar({ ev }) {
  const pct = Math.min((ev / 0.60) * 100, 100);
  const color = ev >= 0.20 ? '#10B981' : ev >= 0.10 ? '#F59E0B' : '#71717A';
  return (
    <div className="flex items-center gap-2">
      <div className="w-14 h-1.5 rounded-full bg-zinc-800">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span
        className="text-sm tabular-nums text-zinc-300"
        style={{ fontFamily: 'JetBrains Mono, monospace' }}
      >
        +{(ev * 100).toFixed(1)}%
      </span>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: 'bg-emerald-500/15 text-emerald-400',
    won: 'bg-blue-500/15 text-blue-400',
    lost: 'bg-red-500/15 text-red-400',
  };
  const labels = { active: t.status.active, won: t.status.won, lost: t.status.lost };
  return (
    <span className={`px-1.5 py-0.5 text-xs rounded ${map[status] || 'bg-zinc-500/15 text-zinc-400'}`}>
      {labels[status] || status}
    </span>
  );
}

export default function LiveSignals() {
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sportFilter, setSportFilter] = useState('');
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);

  const fetchSignals = () => {
    setLoading(true);
    const query = sportFilter ? `?sport=${sportFilter}` : '';
    apiGet(`/live-signals${query}`)
      .then(setSignals)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchSignals();
  }, [sportFilter]);

  const runEngine = async () => {
    setRunning(true);
    setRunResult(null);
    try {
      const result = await apiPost('/engine/live/run');
      setRunResult(result);
      fetchSignals();
    } catch (e) {
      setRunResult({ error: e.message });
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-5 fade-in">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1
            className="text-4xl font-bold text-zinc-50 tracking-tight flex items-center gap-3"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {t.signals.live_title}
            <span className="w-2.5 h-2.5 rounded-full bg-red-500 live-dot" />
          </h1>
          <p className="text-zinc-400 mt-1 text-sm">{t.signals.live_subtitle}</p>
        </div>
        <button
          data-testid="run-live-engine-btn"
          onClick={runEngine}
          disabled={running}
          className="flex items-center gap-2 px-4 py-2.5 rounded-sm text-sm font-medium transition-colors disabled:opacity-50"
          style={{ background: '#EF4444', color: '#FFF' }}
          onMouseEnter={(e) => !running && (e.currentTarget.style.background = '#DC2626')}
          onMouseLeave={(e) => !running && (e.currentTarget.style.background = '#EF4444')}
        >
          {running ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <RefreshCw size={14} />
          )}
          {running ? t.signals.running : t.signals.run_live}
        </button>
      </div>

      {/* Engine run result */}
      {runResult && !runResult.error && (
        <div
          data-testid="live-engine-run-result"
          className="flex items-center gap-4 px-4 py-3 rounded-sm text-sm"
          style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}
        >
          <span className="text-red-400 font-medium">{t.signals.live_engine_complete}</span>
          <span className="text-zinc-400">
            {runResult.matches_analyzed} {t.signals.matches_analyzed}
          </span>
          <span className="text-zinc-400">
            {runResult.signals_created} {t.signals.new_signals}
          </span>
          <span className="text-zinc-400">
            {runResult.signals_updated} {t.signals.updated}
          </span>
          <span
            className="text-zinc-500 ml-auto"
            style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '11px' }}
          >
            {runResult.duration_ms}ms
          </span>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3">
        <select
          data-testid="filter-sport-live"
          value={sportFilter}
          onChange={(e) => setSportFilter(e.target.value)}
          className="px-3 py-2 text-sm rounded-sm text-zinc-300 focus:outline-none focus:ring-1 focus:ring-red-500"
          style={{ background: '#18181B', border: '1px solid #27272A' }}
        >
          <option value="">{t.signals.all_sports}</option>
          <option value="tennis">{t.common.tennis}</option>
          <option value="table_tennis">{t.common.table_tennis}</option>
        </select>
        <span className="ml-auto text-xs text-zinc-500">
          {signals.length} {t.signals.total_signals}
        </span>
      </div>

      {/* Table */}
      <div
        className="rounded-sm overflow-hidden"
        style={{ background: '#18181B', border: '1px solid #27272A' }}
      >
        {loading ? (
          <div className="flex items-center gap-2 justify-center py-16 text-zinc-500">
            <Loader2 size={18} className="animate-spin" />
            <span>{t.common.loading}</span>
          </div>
        ) : error ? (
          <div className="text-red-400 text-center py-10">{t.common.error}</div>
        ) : signals.length === 0 ? (
          <div className="text-zinc-600 text-center py-16 text-sm">{t.signals.no_signals}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid #27272A' }}>
                  {[
                    t.signals.match_live,
                    t.signals.score,
                    t.signals.sport,
                    t.signals.market,
                    t.signals.selection,
                    t.signals.bookmaker,
                    t.signals.odds_live,
                    t.signals.odds_fair,
                    t.signals.ev_pct,
                    t.signals.momentum,
                    t.signals.pressure,
                    t.signals.status,
                    '',
                  ].map((h, i) => (
                    <th
                      key={i}
                      className={`py-3 text-xs uppercase tracking-[0.08em] text-zinc-500 font-medium whitespace-nowrap ${
                        i === 0 ? 'text-left px-5' : i >= 6 && i <= 8 ? 'text-right px-4' : 'text-left px-4'
                      }`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {signals.map((sig) => (
                  <tr
                    key={sig.id}
                    data-testid={`live-signal-row-${sig.id}`}
                    className="transition-colors"
                    style={{ borderBottom: '1px solid #1F1F22' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(39,39,42,0.4)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <td className="px-5 py-3 font-medium text-zinc-200 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-500 live-dot flex-shrink-0" />
                        {sig.match_name}
                      </div>
                    </td>
                    <td
                      className="px-4 py-3 text-emerald-400 whitespace-nowrap"
                      style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '11px' }}
                    >
                      {sig.current_score}
                    </td>
                    <td className="px-4 py-3">
                      <SportBadge sport={sig.sport} />
                    </td>
                    <td className="px-4 py-3 text-zinc-400 text-xs whitespace-nowrap">
                      {sig.market_type}
                    </td>
                    <td className="px-4 py-3 text-zinc-300 font-medium">{sig.selection_name}</td>
                    <td className="px-4 py-3 text-zinc-500 text-xs whitespace-nowrap">{sig.bookmaker_name}</td>
                    <td
                      className="px-4 py-3 text-right text-emerald-400"
                      style={{ fontFamily: 'JetBrains Mono, monospace' }}
                    >
                      {sig.live_odds?.toFixed(2)}
                    </td>
                    <td
                      className="px-4 py-3 text-right text-zinc-500"
                      style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '12px' }}
                    >
                      {sig.fair_live_odds?.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <EvBar ev={sig.expected_value} />
                    </td>
                    <td className="px-4 py-3">
                      <MomentumBadge score={sig.momentum_score} />
                    </td>
                    <td className="px-4 py-3">
                      <PressureBar score={sig.pressure_score} />
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={sig.signal_status} />
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/incontri/${sig.match_id}`}
                        data-testid={`live-signal-match-link-${sig.id}`}
                        className="text-zinc-600 hover:text-red-400 transition-colors"
                      >
                        <ExternalLink size={13} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
